import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { sendGelatoTransactionFromServer, sendTransactionFromServer } from '@/features/agent/api';
import { useAgentStore } from '@/features/agent/store';
import { GelatoStatusResponse, TaskState } from '@/types/gelato';
import {
  getUserTaskReceiptFromTaskStatus,
  getUserTaskResponse,
  listenToTaskStatus,
  pollTaskStatus,
} from '@/utils/gelato';
import { JsonRpcProvider, TransactionResponse } from '@ethersproject/providers';
import { AxiosError } from 'axios';
import { ContractReceipt } from 'ethers';
import { getMappedRevertReason } from './error';

/**
 * Callback hooks for different stages of Gelato transaction
 */
export interface GelatoTransactionHooks {
  onStart?: (taskId: string) => void;
  onHasTransactionHash?: (taskId: string, transactionHash?: string, transactionResponse?: TransactionResponse) => void;
  onSuccess?: (taskId: string, transactionHash?: string) => void;
  onError?: (error: string, taskId?: string, taskReceipt?: ContractReceipt) => void;
}

/**
 * Parameters for sending Gelato transaction and waiting for completion
 */
export interface SendGelatoTransactionParams {
  chainId: number;
  batchData: string;
  targetContract: string;
  userAddr: string; // Add userAddr parameter
  provider?: JsonRpcProvider;
  hooks?: GelatoTransactionHooks;
}

export type GelatoTransactionResult =
  | {
      success: true;
      receipt?: ContractReceipt;
      taskId: string;
      response?: TransactionResponse;
    }
  | {
      success: false;
      error?: string;
      taskId?: string;
    };

/**
 * Common interface for task result processing
 */
interface TaskResultProcessor {
  processResult: (provider: JsonRpcProvider, statusResponse: GelatoStatusResponse) => Promise<TaskResult>;
  handleSuccess: (result: TaskResult, taskId: string, hooks?: GelatoTransactionHooks) => GelatoTransactionResult;
}

/**
 * Task status interface
 */
interface TaskStatus {
  taskState: TaskState;
  transactionHash?: string;
  lastCheckMessage?: string;
}

/**
 * Task result interface
 */
interface TaskResult {
  success: boolean;
  status?: TaskStatus;
  transactionReceipt?: ContractReceipt;
  response?: TransactionResponse;
  reason?: string;
}

/**
 * Validates JWT token from agent store
 * @param userAddr - User address to get JWT token for
 * @param startTime - Start time for logging
 * @param hooks - Callback hooks
 * @returns JWT token or null if validation fails
 */
async function validateJwtToken(
  userAddr: string,
  startTime: number,
  hooks?: GelatoTransactionHooks,
): Promise<string | null> {
  const jwtTokenStartTime = Date.now();
  const agentSettings = useAgentStore.getState().userAgentSettings[userAddr];
  const jwtToken = agentSettings?.jwtToken?.token;

  if (!jwtToken) {
    const error = 'JWT token is empty';
    console.error('❌ [Gelato Transaction] JWT token validation failed', {
      durationS: (Date.now() - startTime) / 1000,
      error,
    });
    hooks?.onError?.(error);
    return null;
  }

  console.log('✅ [Gelato Transaction] JWT token validated', {
    durationS: (Date.now() - jwtTokenStartTime) / 1000,
  });

  return jwtToken;
}

/**
 * Send transaction to Gelato API and get task ID
 * @param params - Transaction parameters
 * @param jwtToken - JWT token
 * @param startTime - Start time for duration calculation
 * @param hooks - Callback hooks
 * @returns Task ID and API duration or null if failed
 */
async function sendTransactionToGelato(
  params: { chainId: number; batchData: string; targetContract: string },
  jwtToken: string,
  startTime: number,
  hooks?: GelatoTransactionHooks,
): Promise<{ taskId: string; apiDurationS: number } | null> {
  const apiStartTime = Date.now();
  console.log('📤 [Gelato Transaction] Sending transaction to Gelato API...');

  const taskIdResp = await sendGelatoTransactionFromServer(params, jwtToken);
  const apiDurationS = (Date.now() - apiStartTime) / 1000;

  console.log('✅ [Gelato Transaction] API response received', {
    durationS: apiDurationS,
    response: taskIdResp?.data,
  });

  const taskId = taskIdResp?.data.data?.taskId;
  if (!taskId) {
    const error = 'Task ID is empty';
    console.error('❌ [Gelato Transaction] Task ID validation failed', {
      totalDurationS: (Date.now() - startTime) / 1000,
      apiDurationS,
      error,
      response: taskIdResp?.data,
    });
    hooks?.onError?.(error);
    return null;
  }

  console.log('🎯 [Gelato Transaction] Task ID obtained', {
    taskId,
    totalDurationS: (Date.now() - startTime) / 1000,
    apiDurationS,
  });

  return { taskId, apiDurationS };
}

const hiddenErrors = ['Task Failed After 5 Retries:', 'Target contract call failed with data:'];

export function parseGelatoError(orginError: string) {
  console.log('🚀 ~ passError ~ orginError:', orginError);
  let error = orginError;
  if (hiddenErrors.some((item) => error.includes(item))) {
    let finalError = error;
    for (const hiddenE of hiddenErrors) {
      if (finalError.includes(hiddenE)) {
        const errorParts = finalError.split(hiddenE);
        finalError = errorParts.length > 1 ? errorParts[1].trim() : finalError;
      }
    }
    error = finalError;
    error = getMappedRevertReason(error);
  }
  return error;
}

/**
 * Poll task status and handle common error cases
 * @param taskId - Task ID
 * @param startTime - Start time for duration calculation
 * @param apiDurationS - API call duration
 * @param hooks - Callback hooks
 * @param pollHooks - Optional hooks for polling
 * @returns Task status and poll duration or null if failed
 */
/**
 * Listen to task status using WebSocket events instead of polling
 */
async function listenAndValidateTaskStatus(
  taskId: string,
  startTime: number,
  apiDurationS: number,
  hooks?: GelatoTransactionHooks,
): Promise<{ statusResponse: GelatoStatusResponse; pollDurationS: number } | null> {
  const pollStartTime = Date.now();
  console.log('🔄 [Gelato Transaction] Listening to task status...', { taskId });

  try {
    const statusResponse = await listenToTaskStatus(taskId, {
      onHasTransactionHash: (taskId: string, transactionHash: string) => {
        console.log('📝 [Gelato Transaction] Transaction hash received', { taskId, transactionHash });
        hooks?.onHasTransactionHash?.(taskId, transactionHash);
      },
      onTaskCompleted: (taskId: string, statusResponse: GelatoStatusResponse) => {
        console.log('✅ [Gelato Transaction] Task completed', {
          taskId,
          taskState: statusResponse.taskState,
          transactionHash: statusResponse.transactionHash,
        });
      },
      onTaskFailed: (taskId: string, statusResponse: GelatoStatusResponse, reason?: string) => {
        const error = parseGelatoError(reason || statusResponse.lastCheckMessage || 'Task failed');
        console.log('❌4 [Gelato Transaction] Task failed', {
          taskId,
          error: error || reason || statusResponse.lastCheckMessage,
          totalDurationS: (Date.now() - startTime) / 1000,
          breakdown: {
            apiCallS: apiDurationS,
            taskListenS: (Date.now() - pollStartTime) / 1000,
          },
        });
        hooks?.onError?.(parseGelatoError(reason || statusResponse.lastCheckMessage || 'Task failed'), taskId);
      },
    });

    const pollDurationS = (Date.now() - pollStartTime) / 1000;

    console.log('✅ [Gelato Transaction] Task status received', {
      taskId,
      pollDurationS,
      taskState: statusResponse.taskState,
      transactionHash: statusResponse.transactionHash,
    });

    // Check for task failure
    if (
      statusResponse.taskState === TaskState.Cancelled ||
      statusResponse.taskState === TaskState.ExecReverted ||
      !statusResponse.transactionHash
    ) {
      console.log('❌1 [Gelato Transaction] Task failed or was cancelled', {
        taskId,
        error: statusResponse.lastCheckMessage,
        totalDurationS: (Date.now() - startTime) / 1000,
        breakdown: {
          apiCallS: apiDurationS,
          taskListenS: pollDurationS,
        },
      });

      hooks?.onError?.(parseGelatoError(statusResponse.lastCheckMessage || 'Task failed or was cancelled'), taskId);
      return null;
    }

    return { statusResponse, pollDurationS };
  } catch (error: unknown) {
    const pollDurationS = (Date.now() - pollStartTime) / 1000;

    if (error && typeof error === 'object' && 'status' in error) {
      // This is a task failure with status
      const statusResponse = (error as { status: GelatoStatusResponse }).status;

      const errorMsg = parseGelatoError(statusResponse.lastCheckMessage || 'Task failed');
      console.log('❌2 [Gelato Transaction] Task failed', {
        taskId,
        pollDurationS,
        taskState: statusResponse.taskState,
        error: errorMsg || statusResponse.lastCheckMessage,
      });

      hooks?.onError?.(errorMsg, taskId);
      return null;
    } else {
      // This is an actual error
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ [Gelato Transaction] Error listening to task status', {
        taskId,
        error: errorMessage,
        totalDurationS: (Date.now() - startTime) / 1000,
      });
      hooks?.onError?.(errorMessage || 'Failed to listen to task status', taskId);
      return null;
    }
  }
}
console.log('🚀 ~ listenAndValidateTaskStatus ~ listenAndValidateTaskStatus:', listenAndValidateTaskStatus);

/**
 * Poll task status and handle common error cases
 * @param taskId - Task ID
 * @param startTime - Start time for duration calculation
 * @param apiDurationS - API call duration
 * @param hooks - Callback hooks
 * @param pollHooks - Optional hooks for polling
 * @returns Task status and poll duration or null if failed
 */
async function pollAndValidateTaskStatus(
  taskId: string,
  startTime: number,
  apiDurationS: number,
  hooks?: GelatoTransactionHooks,
  pollHooks?: { onHasTransactionHash?: (taskId: string, transactionHash: string) => Promise<void> },
): Promise<{ statusResponse: GelatoStatusResponse; pollDurationS: number } | null> {
  const pollStartTime = Date.now();
  console.log('🔄 [Gelato Transaction] Polling task status...', { taskId });

  const statusResponse = await pollTaskStatus(taskId, pollHooks);
  const pollDurationS = (Date.now() - pollStartTime) / 1000;

  console.log('✅ [Gelato Transaction] Task status received', {
    taskId,
    pollDurationS,
    taskState: statusResponse.taskState,
    transactionHash: statusResponse.transactionHash,
  });

  // Check for task failure
  if (
    statusResponse.taskState === TaskState.Cancelled ||
    statusResponse.taskState === TaskState.ExecReverted ||
    !statusResponse.transactionHash
  ) {
    console.log('❌ [Gelato Transaction] Task failed or was cancelled', {
      taskId,
      error: statusResponse.lastCheckMessage,
      totalDurationS: (Date.now() - startTime) / 1000,
      breakdown: {
        apiCallS: apiDurationS,
        taskPollS: pollDurationS,
      },
    });

    hooks?.onError?.(parseGelatoError(statusResponse.lastCheckMessage || 'Task failed or was cancelled'), taskId);
    return null;
  }

  return { statusResponse: statusResponse, pollDurationS };
}
console.log('🚀 ~ pollAndValidateTaskStatus ~ pollAndValidateTaskStatus:', pollAndValidateTaskStatus);

/**
 * Handle common error cases in try-catch blocks
 * @param error - Error object
 * @param startTime - Start time for duration calculation
 * @param hooks - Callback hooks
 * @returns Error result
 */
function handleCommonError(error: unknown, startTime: number, hooks?: GelatoTransactionHooks): GelatoTransactionResult {
  let errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
  if (error instanceof AxiosError) {
    errorMessage = error.response?.data?.message || error.response?.data?.msg || errorMessage;
  }

  const totalDurationS = (Date.now() - startTime) / 1000;
  console.error('💥 [Gelato Transaction] Unexpected error occurred', {
    totalDurationS,
    error: errorMessage,
    errorType: error instanceof AxiosError ? 'AxiosError' : error instanceof Error ? 'Error' : 'Unknown',
    stack: error instanceof Error ? error.stack : undefined,
  });

  hooks?.onError?.(errorMessage);

  return {
    success: false,
    error: errorMessage,
  };
}

/**
 * Core Gelato transaction processing logic
 * @param params - Transaction parameters
 * @param processor - Task result processor
 * @returns Promise with transaction result
 */
async function processGelatoTransaction(
  params: SendGelatoTransactionParams,
  processor: TaskResultProcessor,
): Promise<GelatoTransactionResult> {
  const { chainId, batchData, targetContract, userAddr, provider, hooks } = params;

  // Start timing
  const startTime = Date.now();
  console.log('🚀 [Gelato Transaction] Started', {
    chainId,
    targetContract,
    timestamp: new Date().toISOString(),
  });

  try {
    // Step 1: Validate JWT token
    const jwtToken = await validateJwtToken(userAddr, startTime, hooks);
    if (!jwtToken) {
      return { success: false, error: 'JWT token is empty' };
    }

    if (DAPP_CHAIN_CONFIGS[chainId]?._1CTTxRequestType === 'gelato') {
      // Step 2: Send transaction to Gelato
      const apiResult = await sendTransactionToGelato(
        { chainId, batchData, targetContract },
        jwtToken,
        startTime,
        hooks,
      );
      if (!apiResult) {
        return { success: false, error: 'Task ID is empty' };
      }

      const { taskId, apiDurationS } = apiResult;

      // Notify transaction started
      hooks?.onStart?.(taskId);

      // Step 3: Listen to task status using WebSocket events
      const pollResult = await listenAndValidateTaskStatus(taskId, startTime, apiDurationS, hooks);

      // // Step 3: Poll task status with optional hooks
      // const pollHooks =
      //   processor === receiptProcessor
      //     ? undefined
      //     : {
      //         onHasTransactionHash: async (taskId: string, transactionHash: string) => {
      //           const response = await provider?.getTransaction(transactionHash);
      //           hooks?.onHasTransactionHash?.(taskId, transactionHash, response);
      //         },
      //       };

      // const pollResult = await pollAndValidateTaskStatus(taskId, startTime, apiDurationS, hooks, pollHooks);
      if (!pollResult) {
        return {
          success: false,
          taskId,
          error: 'Task failed or was cancelled',
        };
      }

      const { statusResponse: statusResponse, pollDurationS } = pollResult;

      // Step 4: Process task result
      const receiptStartTime = Date.now();
      console.log('⏳ [Gelato Transaction] Waiting for task receipt...', {
        taskId,
        totalDurationS: (Date.now() - startTime) / 1000,
        statusResponse,
      });

      // Validate provider before processing
      if (!provider) {
        const error = 'Provider is required for processing task result';
        console.error('❌ [Gelato Transaction] Provider validation failed', {
          taskId,
          totalDurationS: (Date.now() - startTime) / 1000,
          error,
        });
        hooks?.onError?.(error, taskId);
        return {
          success: false,
          error,
          taskId,
        };
      }

      const taskResult = await processor.processResult(provider, statusResponse);
      const receiptDurationS = (Date.now() - receiptStartTime) / 1000;

      console.log('📋 [Gelato Transaction] Task receipt received', {
        taskId,
        receiptDurationS,
        pollDurationS,
        totalDurationS: (Date.now() - startTime) / 1000,
        receipt: {
          success: taskResult.success,
          status: taskResult?.status?.taskState,
          transactionHash: taskResult?.status?.transactionHash,
        },
      });

      // Step 5: Handle success or failure
      if (taskResult.success) {
        return processor.handleSuccess(taskResult, taskId, hooks);
      } else {
        // Task failed
        let error = taskResult?.reason || taskResult?.status?.lastCheckMessage || 'Transaction failed';

        const totalDurationS = (Date.now() - startTime) / 1000;
        error = parseGelatoError(error);

        console.error('❌3 [Gelato Transaction] Task failed', {
          taskId,
          totalDurationS,
          error,
          reason: taskResult?.reason,
          lastCheckMessage: taskResult?.status?.lastCheckMessage,
          breakdown: {
            apiCallS: apiDurationS,
            receiptWaitS: receiptDurationS,
            taskPollS: pollDurationS,
          },
        });

        hooks?.onError?.(error, taskId, taskResult.transactionReceipt);
        return {
          success: false,
          error,
          taskId,
        };
      }
    } else {
      // Step 2: Send transaction to Gelato
      const apiResult = await sendTransactionFromServer(
        { chainID: chainId, calldata: batchData, to: targetContract, userAddr },
        jwtToken,
      );

      console.log('🚀 ~ processGelatoTransaction ~ apiResult:', apiResult);
      if (apiResult?.data?.data?.txHash) {
        hooks?.onStart?.(apiResult?.data?.data?.txHash);
        const txHash = apiResult?.data?.data?.txHash;
        // const publicClient = getPublicClient(wagmiConfig);
        if (!provider) {
          return { success: false, error: 'Provider is empty' };
        }
        const transaction = await provider.getTransaction(txHash);
        console.log('🚀 ~ processGelatoTransaction ~ transaction:', transaction);

        // const receipt = await transaction.wait();
        if (transaction) {
          hooks?.onSuccess?.(txHash);
          return {
            success: true,
            taskId: txHash,
            response: transaction,
          };
        } else {
          const error = 'Transaction execution failed';
          hooks?.onError?.(error, txHash);
          return {
            success: false,
            error,
            taskId: txHash,
          };
        }
      }

      return { success: false, error: 'txHash is empty' };
    }
  } catch (error: unknown) {
    console.log('🚀 ~ processGelatoTransaction ~ error:', error);
    return handleCommonError(error, startTime, hooks);
  }
}

/**
 * Task result processor for receipt-based transactions
 */
const receiptProcessor: TaskResultProcessor = {
  processResult: async (provider: JsonRpcProvider, statusResponse: GelatoStatusResponse) => {
    return await getUserTaskReceiptFromTaskStatus(provider, statusResponse);
  },
  handleSuccess: (taskReceipt: TaskResult, taskId: string, hooks?: GelatoTransactionHooks) => {
    if (taskReceipt.transactionReceipt) {
      const receipt = taskReceipt.transactionReceipt;
      const transactionHash = taskReceipt?.status?.transactionHash;

      if (receipt.status === 1) {
        // Transaction successful

        hooks?.onSuccess?.(taskId, transactionHash);
        return {
          success: true,
          receipt,
          taskId,
        };
      } else {
        // Transaction failed
        const error = 'Transaction execution failed';

        hooks?.onError?.(error, taskId);
        return {
          success: false,
          error,
          taskId,
        };
      }
    }
    return {
      success: false,
      error: 'No transaction receipt',
      taskId,
    };
  },
};

/**
 * Task result processor for response-based transactions
 */
const responseProcessor: TaskResultProcessor = {
  processResult: async (provider: JsonRpcProvider, status: GelatoStatusResponse) => {
    return await getUserTaskResponse(provider, status);
  },
  handleSuccess: (taskResponse: TaskResult, taskId: string) => {
    if (taskResponse.response) {
      const response = taskResponse.response;
      const transactionHash = taskResponse?.status?.transactionHash;

      if (response.hash) {
        // Transaction successful
        console.log('🎉 [Gelato Transaction] Transaction successful', {
          taskId,
          transactionHash,
        });
      }

      return {
        success: true,
        taskId,
        response,
      };
    }
    return {
      success: false,
      error: 'No transaction response',
      taskId,
    };
  },
};

/**
 * Send Gelato transaction and wait for completion with callback hooks
 * @param params - Transaction parameters including hooks for different stages
 * @returns Promise with transaction result
 */
export async function sendGelatoTransactionAndWait(
  params: SendGelatoTransactionParams,
): Promise<GelatoTransactionResult> {
  return processGelatoTransaction(params, receiptProcessor);
}

/**
 * Send Gelato transaction and get response with callback hooks
 * @param params - Transaction parameters including hooks for different stages
 * @returns Promise with transaction result
 */
export async function sendGelatoTransaction(params: SendGelatoTransactionParams): Promise<GelatoTransactionResult> {
  return processGelatoTransaction(params, responseProcessor);
}
